import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OrganisationInvitationInfo } from '../types/api';
import apiService from '../services/api';
import { VERSION_CONDITIONS, URL_CONDITIONS, URL_CONFIDENTIALITE } from '../constants/legal';
import toast from 'react-hot-toast';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { getApiErrorMessage } from '../constants/messages';

const OrganisationInvitationAcceptPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [invitation, setInvitation] = useState<OrganisationInvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    prenom: '',
    nom: '',
    mot_de_passe: '',
    confirmPassword: '',
    conditions: false,
  });

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        toast.error(t('invitation.tokenMissing'));
        navigate('/login');
        return;
      }

      try {
        const invitationData = await apiService.getOrganisationInvitationInfo(token);
        setInvitation(invitationData);
      } catch (error: unknown) {
        toast.error(t('invitation.invalidOrExpired'));
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [token, navigate]);

  const handleAccept = async () => {
    if (!token || !invitation) return;

    setAccepting(true);
    try {
      const response = await apiService.acceptOrganisationInvitation({
        token_invitation: token,
      });

      toast.success(t('invitation.joinedSuccess', { name: invitation.organisation_nom }));

      // If access_token is provided, it means a new account was created
      if (response.access_token) {
        localStorage.setItem('auth_status', 'true');
      }

      navigate(`/organisations/${response.organisation_id}/dashboard`);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('invitation.acceptError'));
    } finally {
      setAccepting(false);
    }
  };

  const handleAcceptAsNewUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newUserForm.mot_de_passe !== newUserForm.confirmPassword) {
      toast.error(t('invitation.newUser.passwordMismatch'));
      return;
    }

    if (newUserForm.mot_de_passe.length < 8) {
      toast.error(t('invitation.newUser.passwordMinLength'));
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newUserForm.mot_de_passe)) {
      toast.error(t('invitation.newUser.passwordComplexity'));
      return;
    }

    setAccepting(true);
    try {
      const response = await apiService.acceptOrganisationInvitation({
        token_invitation: token!,
        prenom: newUserForm.prenom,
        nom: newUserForm.nom,
        mot_de_passe: newUserForm.mot_de_passe,
        conditions_version: VERSION_CONDITIONS,
      });

      // Check if verification is required
      if (response.verification_required && response.verification_sent) {
        toast.success(t('invitation.newUser.verifyEmail'));
        // Redirect to verification page with email and org ID
        navigate('/verify-account', { state: { email: invitation?.email_invite || '', orgId: response.organisation_id } });
      } else {
        toast.success(t('invitation.newUser.accountCreated', { name: invitation?.organisation_nom }));

        if (response.access_token) {
          localStorage.setItem('auth_status', 'true');
        }

        navigate(`/organisations/${response.organisation_id}/dashboard`);
      }
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error) || t('invitation.newUser.createError'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-text-secondary">{t('invitation.loading')}</p>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-accent-400" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-text-primary">
          {t('invitation.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          {t('invitation.subtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
          {/* Invitation Details */}
          <div className="mb-6 p-4 bg-blue-500/12 rounded-lg">
            <h3 className="text-lg font-medium text-blue-400">
              {invitation.organisation_nom}
            </h3>
            <p className="mt-1 text-sm text-blue-400">
              {t('invitation.role')} <span className="font-semibold">{invitation.role_organisation.toUpperCase()}</span>
            </p>
            <p className="mt-1 text-xs text-accent-400">
              {t('invitation.invitedBy')} {invitation.inviteur_prenom} {invitation.inviteur_nom}
            </p>
            <p className="mt-1 text-xs text-accent-400">
              {t('invitation.expiresOn')} {new Date(invitation.date_expiration).toLocaleDateString()}
            </p>
          </div>

          {/* Existing User */}
          {invitation.user_exists ? (
            <div>
              <p className="text-sm text-text-secondary mb-4">
                {t('invitation.existingUser.accountExists', { email: invitation.email_invite })}
                {apiService.isAuthenticated() ? (
                  <span> {t('invitation.existingUser.clickToAccept')}</span>
                ) : (
                  <span dangerouslySetInnerHTML={{ __html: ' ' + (t('invitation.existingUser.loginToAccept') as string).replace('<a>', '<a href="/login" class="text-accent-600 hover:text-accent-500 font-medium">') }} />
                )}
              </p>
              {apiService.isAuthenticated() && (
                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50"
                >
                  {accepting ? t('invitation.existingUser.accepting') : t('invitation.existingUser.acceptButton')}
                </button>
              )}
            </div>
          ) : (
            /* New User Registration */
            <div>
              <p className="text-sm text-text-secondary mb-4">
                {t('invitation.newUser.createAccount')}
              </p>
              <form onSubmit={handleAcceptAsNewUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary">
                    {t('invitation.newUser.firstName')}
                  </label>
                  <input
                    type="text"
                    required
                    value={newUserForm.prenom}
                    onChange={(e) => setNewUserForm({ ...newUserForm, prenom: e.target.value })}
                    className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-2 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary">
                    {t('invitation.newUser.lastName')}
                  </label>
                  <input
                    type="text"
                    required
                    value={newUserForm.nom}
                    onChange={(e) => setNewUserForm({ ...newUserForm, nom: e.target.value })}
                    className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-2 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary">
                    {t('invitation.newUser.password')}
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={newUserForm.mot_de_passe}
                    onChange={(e) => setNewUserForm({ ...newUserForm, mot_de_passe: e.target.value })}
                    className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-2 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-secondary">
                    {t('invitation.newUser.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    required
                    value={newUserForm.confirmPassword}
                    onChange={(e) => setNewUserForm({ ...newUserForm, confirmPassword: e.target.value })}
                    className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-2 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                {/* Acceptation des conditions. Uniquement ici : ce formulaire CREE un
                    compte. La branche « membre deja inscrit », plus haut, ne recree rien
                    et n'a donc rien a faire accepter. */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    checked={newUserForm.conditions}
                    onChange={(e) => setNewUserForm({ ...newUserForm, conditions: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-border-1 text-accent-500 focus:ring-accent-500"
                  />
                  <span className="text-xs text-text-secondary">
                    {t('invitation.terms.text')}{' '}
                    <a href={URL_CONDITIONS} target="_blank" rel="noopener noreferrer"
                       className="font-medium text-accent-600 hover:text-accent-500">
                      {t('invitation.terms.termsOfService')}
                    </a>{' '}
                    <span className="text-text-tertiary">
                      ({t('invitation.terms.version', { version: VERSION_CONDITIONS })})
                    </span>{' '}
                    {t('invitation.terms.and')}{' '}
                    <a href={URL_CONFIDENTIALITE} target="_blank" rel="noopener noreferrer"
                       className="font-medium text-accent-600 hover:text-accent-500">
                      {t('invitation.terms.privacyPolicy')}
                    </a>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={accepting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50"
                >
                  {accepting ? t('invitation.newUser.creating') : t('invitation.newUser.createAndJoin')}
                </button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-sm text-text-secondary">
                  {t('invitation.newUser.alreadyHaveAccount')}{' '}
                  <a href="/login" className="font-medium text-accent-400 hover:text-accent-300">
                    {t('invitation.newUser.login')}
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganisationInvitationAcceptPage;
